import { useMemo } from 'react';
import { useSources } from './useSource';
import type { Source } from '../services/source.service';

/**
 * High enough that one page holds every source a tenant realistically has, so the
 * index answers on its own and nothing has to be fetched individually. Deliberately
 * not the `useSources` default of 50: sharing that cache key would save a request on
 * the two pages already using it, but would leave anyone past the 50th source
 * fetched one at a time — which is the thing this exists to avoid.
 */
const INDEX_PAGE_SIZE = 500;

export type SourceIndex = {
  byId: Map<string, Source>;
  isLoading: boolean;
  /**
   * Whether every source the tenant has is in `byId`. When true, an id that isn't
   * there does not exist, and asking the server for it individually would only buy
   * a 404 — so callers can go straight to their fallback.
   */
  isComplete: boolean;
};

/**
 * One request that answers "what does this source look like" for every source on the
 * page, instead of one request per source.
 *
 * The list is already cached for five minutes and keyed on its params, so every
 * caller on a page shares a single fetch no matter how many of them there are. Meant
 * to be consumed through SourceIcon rather than directly.
 */
export function useSourceIndex(options?: { enabled?: boolean }): SourceIndex {
  const { data, isLoading } = useSources({
    page: 1,
    pageSize: INDEX_PAGE_SIZE,
    enabled: options?.enabled ?? true,
  });

  return useMemo(() => deriveSourceIndex(data, isLoading), [data, isLoading]);
}

/** Exported for tests; use useSourceIndex in components. */
export function deriveSourceIndex(
  data: { records?: Source[]; total_records?: number } | undefined,
  isLoading: boolean,
): SourceIndex {
  const records = data?.records || [];
  const total = data?.total_records ?? records.length;
  return {
    byId: new Map(records.map((s) => [s.id, s])),
    isLoading,
    // `total_records` can exceed what one page returned; only then is an id we
    // don't hold genuinely unknown rather than genuinely absent.
    isComplete: !isLoading && !!data && records.length >= total,
  };
}

/**
 * Whether one source has to be fetched on its own, given what the index knows.
 *
 * The whole point of the index is that this is usually false. It is true only while
 * the list hasn't arrived yet, or when the list came back truncated and this id fell
 * outside it. A complete list that lacks the id is an answer — the source is gone —
 * and asking the server would buy nothing but a 404.
 *
 * Exported for tests; SourceIcon is the only caller.
 */
export function shouldFetchSourceIndividually(args: {
  isVirtual: boolean;
  foundInIndex: boolean;
  index: Pick<SourceIndex, 'isLoading' | 'isComplete'>;
}): boolean {
  const { isVirtual, foundInIndex, index } = args;
  if (isVirtual || foundInIndex) return false;
  if (index.isLoading) return false;
  return !index.isComplete;
}
